import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTonConnectUI, useTonAddress } from "@tonconnect/ui-react";
import { colors } from "../theme";
import { useUser } from "../hooks/useAuth";
import api from "../api";
import { cleanDescription } from "../utils/cleanDescription";

interface Stats {
  wins: number;
  losses: number;
  total: number;
  streak: number;
}

interface ResolvedBet {
  id: number;
  bet_id: string;
  description: string;
  amount: number;
  status: string;
  winner_id: number | null;
  // VEN-50: pile_on bets don't set winner_id; win/loss comes from
  // winner_side matched against the caller's participation side.
  winner_side: string | null;
  my_side: string | null;
  bet_mode: string | null;
  expires_at: string;
}

const DEV_STATS: Stats = { wins: 2, losses: 1, total: 3, streak: 1 };
const DEV_HISTORY: ResolvedBet[] = [
  { id: 3, bet_id: "dev-bet-3", description: "Run 10 km", amount: 500_000_000, status: "resolved", winner_id: 0, winner_side: null, my_side: null, bet_mode: "1v1", expires_at: "2026-02-28T12:00:00Z" },
  { id: 4, bet_id: "dev-bet-4", description: "Swim 1 km", amount: 2_000_000_000, status: "resolved", winner_id: 777, winner_side: null, my_side: null, bet_mode: "1v1", expires_at: "2026-02-25T12:00:00Z" },
];

export default function ProfilePage() {
  const user = useUser();
  const navigate = useNavigate();
  const [tonConnectUI] = useTonConnectUI();
  const walletAddress = useTonAddress(false); // raw address
  // VEN-50 round 3: stats is fetched for telemetry/parity with backend but
  // not consumed in the UI anymore — counters now derive from history so
  // they stay in lock-step with the rendered list. Kept the call to avoid
  // changing the network signature (backend logs assume the hit).
  const [, setStats] = useState<Stats | null>(null);
  const [history, setHistory] = useState<ResolvedBet[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      if (import.meta.env.DEV) {
        try {
          const [statsRes, historyRes] = await Promise.all([
            api.get("/users/me/stats"),
            api.get("/bets", { params: { status: "resolved" } }),
          ]);
          setStats(statsRes.data);
          setHistory(
            Array.isArray(historyRes.data?.bets)
              ? historyRes.data.bets
              : Array.isArray(historyRes.data)
                ? historyRes.data
                : []
          );
          setLoading(false);
          return;
        } catch {
          // Backend not available — use mock data
        }
        setStats(DEV_STATS);
        setHistory(DEV_HISTORY);
        setLoading(false);
        return;
      }

      try {
        const [statsRes, historyRes] = await Promise.all([
          api.get("/users/me/stats"),
          api.get("/bets", { params: { status: "resolved" } }),
        ]);
        setStats(statsRes.data);
        setHistory(
          Array.isArray(historyRes.data?.bets)
            ? historyRes.data.bets
            : Array.isArray(historyRes.data)
              ? historyRes.data
              : []
        );
      } catch {
        // fail silently
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // VEN-50 round 4: keep every resolved row the backend returns — the
  // previous `winner_id != null` filter dropped pile_on bets (those only
  // set winner_side), which made QA's "14 resolved → 9 visible" mismatch.
  // Counter and list now both render off this one list; wins/losses are
  // derived below using winner_id for 1v1 and (my_side === winner_side)
  // for pile_on, so the equation Wins + Losses = Bets holds whenever
  // every row has a determinable outcome.
  const resolvedHistory = history.filter(
    (bet) => ["resolved", "completed", "finished", "expired", "cancelled"].includes(bet.status?.toLowerCase()),
  );

  // Per-bet outcome: `true` = win, `false` = loss, `null` = indeterminate
  // (shouldn't happen for resolved bets but guards against unexpected data).
  const betOutcome = (bet: ResolvedBet): boolean | null => {
    if (!user) return null;
    if (bet.winner_id != null) {
      return bet.winner_id === user.id || bet.winner_id === user.telegram_id;
    }
    if (bet.winner_side && bet.my_side) {
      return bet.my_side === bet.winner_side;
    }
    return null;
  };

  // Calculate Net P&L from resolved bets
  const pnl = resolvedHistory.reduce((sum, bet) => {
    const outcome = betOutcome(bet);
    if (outcome === null) return sum;
    const tons = bet.amount / 1_000_000_000;
    return sum + (outcome ? tons : -tons);
  }, 0);
  const pnlPositive = pnl > 0;
  const pnlNeutral = pnl === 0;

  // Format member since date
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : "";

  // Truncate wallet address: "UQC9...69Y"
  const truncatedWallet = walletAddress
    ? `${walletAddress.slice(0, 4)}...${walletAddress.slice(-3)}`
    : null;

  // VEN-50 round 4: counters derive from the same list the History section
  // renders, using betOutcome() so pile_on rows (winner_id null, decided via
  // winner_side) still contribute to wins/losses. No stats fallback — while
  // auth is loading we show `—` to make the indeterminate state explicit
  // instead of briefly showing stale /users/me/stats numbers.
  const wins = resolvedHistory.filter((bet) => betOutcome(bet) === true).length;
  const losses = resolvedHistory.filter((bet) => betOutcome(bet) === false).length;
  const totalBets = resolvedHistory.length;
  const countersReady = !!user;
  const winsDisplay: number | string = countersReady ? wins : "—";
  const lossesDisplay: number | string = countersReady ? losses : "—";
  const totalDisplay: number | string = countersReady ? totalBets : "—";
  // Win percentage
  const winPct = countersReady && totalBets > 0
    ? Math.round((wins / totalBets) * 100)
    : 0;

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 14, color: colors.grayLight }}>Loading profile...</div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflowY: "auto" }}>
      <div style={{ padding: "24px 16px 16px" }}>

        {/* ---- Avatar + Name ---- */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 24 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 32,
              background: colors.purple,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 800,
              color: colors.white,
              marginBottom: 12,
            }}
          >
            {(user?.first_name?.[0] || "?").toUpperCase()}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>
            @{user?.username || "user"}
          </div>
          <div style={{ fontSize: 13, color: colors.gray }}>
            Member since {memberSince}
          </div>
        </div>

        {/* ---- NET P&L Card ---- */}
        <div
          style={{
            background: colors.surface,
            border: `1px solid ${pnlPositive ? colors.success : colors.danger}`,
            borderRadius: 16,
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: colors.grayLight }}>
            NET P&L
          </span>
          <span
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: pnlNeutral ? colors.grayLight : pnlPositive ? colors.success : colors.danger,
            }}
          >
            {pnlNeutral ? "+0 TON" : `${pnlPositive ? "+" : "\u2212"}${Math.abs(pnl).toFixed(2)} TON`}
          </span>
        </div>

        {/* ---- Stats Grid (4 cols) ---- */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 8,
            marginBottom: 24,
          }}
        >
          <StatBox label="Bets" value={totalDisplay} />
          <StatBox label="Wins ✓" value={winsDisplay} />
          <StatBox label="Losses ✗" value={lossesDisplay} />
          <StatBox label="Win%" value={`${winPct}%`} />
        </div>

        {/* ---- Integrations ---- */}
        <SectionLabel>Integrations</SectionLabel>

        {/* HealthBridge */}
        <HealthBridgeCard />

        {/* TON Wallet */}
        <div
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 14,
            padding: "14px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>💎</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>TON Wallet</div>
              {walletAddress ? (
                <div style={{ fontSize: 12, color: colors.success }}>
                  ✓ {truncatedWallet}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: colors.grayLight }}>Not connected</div>
              )}
            </div>
          </div>
          {walletAddress ? (
            <button
              onClick={() => tonConnectUI.disconnect()}
              style={{
                background: "transparent",
                border: `1px solid ${colors.danger}`,
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                color: colors.danger,
                cursor: "pointer",
              }}
            >
              Disconnect
            </button>
          ) : (
            <button
              onClick={() => tonConnectUI.openModal()}
              style={{
                background: colors.purple,
                border: "none",
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 700,
                color: colors.white,
                cursor: "pointer",
              }}
            >
              Connect Wallet
            </button>
          )}
        </div>

        {/* ---- History ---- */}
        {resolvedHistory.length > 0 && (
          <>
            <SectionLabel>History</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {resolvedHistory.map((bet) => {
                const outcome = betOutcome(bet);
                const won = outcome === true;
                const isCancelledOrExpired = ["cancelled", "expired"].includes(bet.status?.toLowerCase());
                const tons = bet.amount / 1_000_000_000;
                const emoji = getActivityEmoji(bet.description);
                const cleanedDescription = cleanDescription(bet.description);
                const date = new Date(bet.expires_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });

                let amountText: string;
                let amountColor: string;
                if (isCancelledOrExpired) {
                  amountText = "Refunded";
                  amountColor = colors.gray;
                } else {
                  const sign = won ? "+" : "-";
                  amountText = `${sign}${tons % 1 === 0 ? tons.toFixed(0) : tons.toFixed(1)} TON`;
                  amountColor = won ? colors.success : colors.danger;
                }

                return (
                  <button
                    key={bet.bet_id}
                    onClick={() => navigate(`/bet/${bet.bet_id}`)}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom: `1px solid ${colors.border}`,
                      background: "transparent",
                      color: colors.white,
                      cursor: "pointer",
                      border: "none",
                      borderBlockEnd: `1px solid ${colors.border}`,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 18 }}>{emoji}</span>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{cleanedDescription}</div>
                        <div style={{ fontSize: 11, color: colors.gray }}>{date}</div>
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: amountColor,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {amountText}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- Helper components ---- */

function StatBox({ label, value }: { label: string; value: number | string }) {
  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        padding: "12px 8px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: colors.gray }}>{label}</div>
    </div>
  );
}

function MiniStat({ icon, value, label }: { icon: string; value: string; label: string }) {
  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: "8px 4px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 12 }}>{icon}</div>
      <div style={{ fontSize: 13, fontWeight: 800 }}>{value}</div>
      <div style={{ fontSize: 9, color: colors.grayLight, textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: colors.gray,
        textTransform: "uppercase",
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function HealthBridgeCard() {
  const [step, setStep] = useState(0); // 0=closed, 1=download, 2=permissions, 3=code
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ connected: boolean; last_sync_at: string | null } | null>(null);
  const [copied, setCopied] = useState(false);
  const [todayStats, setTodayStats] = useState<{ steps: number; distance_km: number; active_minutes: number; calories: number } | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const IOS_STORE = "https://apps.apple.com/app/healthbridge/id6764058332";
  const ANDROID_STORE = "https://play.google.com/store/apps/details?id=com.eventyrvlad.healthbridge2026";

  const fetchStats = () => {
    api.get("/healthbridge/status").then((res) => {
      setStatus(res.data);
      if (res.data?.connected) {
        api.get("/healthbridge/stats/today").then((r) => setTodayStats(r.data)).catch(() => {});
      }
    }).catch(() => {});
  };

  useEffect(() => { fetchStats(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      fetchStats();
    } finally {
      setTimeout(() => setRefreshing(false), 1000);
    }
  };

  const handleDisconnect = async () => {
    if (disconnecting) return;
    setDisconnecting(true);
    try {
      await api.delete("/healthbridge/disconnect");
      setStatus({ connected: false, last_sync_at: null });
      setTodayStats(null);
      setStep(0);
    } catch {
      // fail silently
    } finally {
      setDisconnecting(false);
    }
  };

  // Poll for connection status when on Step 3 (user pasting code in app)
  // VEN-119: transition to "Connected" as soon as connected=true, even if
  // last_sync_at is not yet set (first sync hasn't happened yet).
  useEffect(() => {
    if (step !== 3) return;
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      api.get("/healthbridge/status").then((res) => {
        if (res.data?.connected) {
          setStatus(res.data);
          setStep(0);
          fetchStats();
          clearInterval(poll);
        }
      }).catch(() => {});
      if (attempts >= 40) clearInterval(poll); // stop after 2 min
    }, 3000);
    return () => clearInterval(poll);
  }, [step]);

  const startConnect = () => setStep(1);

  const generateCode = async () => {
    setLoading(true);
    try {
      const res = await api.post("/healthbridge/generate-code");
      setCode(res.data.code);
      setStep(3);
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (code) {
      navigator.clipboard.writeText(code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const openDeepLink = () => {
    if (code) {
      window.open(`healthbridge://sidebets-connect?code=${encodeURIComponent(code)}`, "_blank");
    }
  };

  // Connected state — compact view with stats
  if (status?.connected && step === 0) {
    return (
      <div style={{ background: colors.surface, border: `1px solid ${colors.success}`, borderRadius: 14, padding: "14px 16px", marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>🏥</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>HealthBridge</div>
              <div style={{ fontSize: 12, color: colors.success }}>
                ✓ Connected{status.last_sync_at ? ` · Last sync ${new Date(status.last_sync_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}` : ""}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              style={{ background: "transparent", border: `1px solid ${colors.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, color: colors.white, cursor: refreshing ? "default" : "pointer", opacity: refreshing ? 0.5 : 1 }}
            >
              {refreshing ? "..." : "↻"}
            </button>
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              style={{ background: "transparent", border: `1px solid ${colors.danger}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: colors.danger, cursor: disconnecting ? "default" : "pointer", opacity: disconnecting ? 0.5 : 1 }}
            >
              {disconnecting ? "..." : "Disconnect"}
            </button>
          </div>
        </div>
        {todayStats && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginTop: 12 }}>
            <MiniStat icon="👣" value={todayStats.steps.toLocaleString()} label="steps" />
            <MiniStat icon="🏃" value={`${todayStats.distance_km < 1 ? todayStats.distance_km.toFixed(2) : todayStats.distance_km.toFixed(1)}km`} label="distance" />
            <MiniStat icon="⏱" value={`${Math.round(todayStats.active_minutes)}m`} label="active" />
            <MiniStat icon="🔥" value={`${todayStats.calories}`} label="kcal" />
          </div>
        )}
      </div>
    );
  }

  // Not connected / onboarding flow
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 14, padding: "14px 16px", marginBottom: 8 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: step > 0 ? 16 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>🏥</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>HealthBridge</div>
            <div style={{ fontSize: 12, color: colors.grayLight }}>Sync Apple Health & Health Connect</div>
          </div>
        </div>
        {step === 0 && (
          <button onClick={startConnect} style={{ background: colors.purple, border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: colors.white, cursor: "pointer" }}>
            Connect
          </button>
        )}
      </div>

      {/* Step indicators */}
      {step > 0 && (
        <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
          {[1, 2, 3].map((s) => (
            <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: s <= step ? colors.purple : colors.border }} />
          ))}
        </div>
      )}

      {/* Step 1: Download App */}
      {step === 1 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Step 1: Download HealthBridge</div>
          <div style={{ fontSize: 12, color: colors.grayLight, marginBottom: 12 }}>
            Install the HealthBridge app to sync your health data with Sidebets.
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <a href={IOS_STORE} target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "10px", fontSize: 12, fontWeight: 600, color: colors.white, textDecoration: "none" }}>
              🍎 App Store
            </a>
            <a href={ANDROID_STORE} target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "10px", fontSize: 12, fontWeight: 600, color: colors.white, textDecoration: "none" }}>
              🤖 Google Play
            </a>
          </div>
          <button onClick={() => setStep(2)} style={{ width: "100%", background: colors.purple, border: "none", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, color: colors.white, cursor: "pointer" }}>
            I have the app → Next
          </button>
        </div>
      )}

      {/* Step 2: Permissions */}
      {step === 2 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Step 2: Allow Health Permissions</div>
          <div style={{ fontSize: 12, color: colors.grayLight, marginBottom: 12 }}>
            Open HealthBridge and grant access to Apple Health or Health Connect. Allow all categories (steps, heart rate, sleep, workouts).
          </div>
          <button onClick={generateCode} disabled={loading} style={{ width: "100%", background: colors.purple, border: "none", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, color: colors.white, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
            {loading ? "Generating code..." : "Done → Get my code"}
          </button>
        </div>
      )}

      {/* Step 3: Paste code */}
      {step === 3 && code && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Step 3: Paste code in HealthBridge</div>
          <div style={{ fontSize: 12, color: colors.grayLight, marginBottom: 8 }}>
            Copy the code below and paste it in HealthBridge app (Connect tab), or tap "Open App".
          </div>
          <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "10px 12px", fontFamily: "monospace", fontSize: 13, color: colors.white, wordBreak: "break-all", marginBottom: 8 }}>
            {code}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={copyCode} style={{ flex: 1, background: "transparent", border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px", fontSize: 12, fontWeight: 600, color: copied ? colors.success : colors.white, cursor: "pointer" }}>
              {copied ? "✓ Copied" : "Copy Code"}
            </button>
            <button onClick={openDeepLink} style={{ flex: 1, background: colors.purple, border: "none", borderRadius: 8, padding: "8px", fontSize: 12, fontWeight: 700, color: colors.white, cursor: "pointer" }}>
              Open App
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getActivityEmoji(description: string | undefined | null): string {
  if (!description) return "🎯";
  const d = description.toLowerCase();
  if (d.includes("run")) return "🏃";
  if (d.includes("cycle") || d.includes("ride") || d.includes("bike")) return "🚴";
  if (d.includes("swim")) return "🏊";
  if (d.includes("workout") || d.includes("gym") || d.includes("session")) return "💪";
  if (d.includes("step")) return "👟";
  return "🎯";
}
